// MapEditorModal.jsx — Full-screen map editor, OpenLayers edition.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// Register all bespoke draw interactions (side-effect imports)
import './customInteractions/index';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { MAP_CONTROL_COLUMN_INSET } from '../mapViewConstants';
import LayerPanel from '../../mapEditor/LayerPanel';
import ViewsPanel from './ViewsPanel';
import MapDrawToolbar from '../../mapEditor/MapDrawToolbar';
import ContextPanel from './ContextPanel';
import useMapEditor from './useMapEditor';
import useDtgRange from './useDtgRange';
import MapTimeline from './MapTimeline';
import { extractMapState, layersVisibleInDtgRange, computeLayerDtgRange } from '../schema/mapPmAdapter';
import { DEFAULT_BASEMAP, DEFAULT_VIEW_ID, OVERLAY_TYPES } from '../schema/mapSchema';
import useEditorKeyboardShortcuts from '../../../hooks/useEditorKeyboardShortcuts';
import EditorTitleBar from '../../editor-shell/EditorTitleBar';
import { RendererSettingsProvider, useRendererSettings } from './milsymbolSettings';
import { MAP_OVERLAY_Z } from './mapEditorModal/mapEditorConstants';
import VignetteOverlay from './mapEditorModal/VignetteOverlay';
import PlacementWizard from './mapEditorModal/PlacementWizard';
import GeocoderSlot from './mapEditorModal/GeocoderSlot';
import { BasemapControl, CoordGridControl, MovementTrailControl } from './mapEditorModal/MapFloatingControls';
import { flyToLonLat } from './mapEditorModal/mapGeometryUtils';
import useOlMapLifecycle from './mapEditorModal/useOlMapLifecycle';
import useSatelliteImagery from './mapEditorModal/useSatelliteImagery';
import useCoordGridOverlay from './mapEditorModal/useCoordGridOverlay';
import useViewportBboxTracking from './mapEditorModal/useViewportBboxTracking';
import useSymbolPlacementState from './mapEditorModal/useSymbolPlacementState';
import useMapViews from './mapEditorModal/useMapViews';
import useSymbolDragDrop from './mapEditorModal/useSymbolDragDrop';

// -- Inner component -----------------------------------------------------------
const MapEditorModalInner = ({
    viewId: initialViewId, dtgSelectedMs, onDtgRangeChange,
    bbox: bboxOverride, onBboxChange,
    title: initialTitle, basemapStyleId: initialBasemapStyleId, activeGridId: initialActiveGridId,
    overlayType: initialOverlayType, onWindowSettingsChange,
    editorState, setEditorState, onClose, onBeforeClose, currentUser, remoteChangeRef, isReadOnly = false,
}) => {
    const mapContainerRef = useRef(null); // div container for OL
    const mapRef = useRef(null); // OL Map instance — see useOlMapLifecycle.js for why this lives here rather than in the hook
    const { settings: msSettings } = useRendererSettings();
    const activeLayerIdRef = useRef(null);
    const vignetteFrameRef = useRef(null);
    const modalRef = useRef(null);
    // useSymbolPlacementState owns pendingTacticalSidc, but useMapEditor (called before that hook,
    // since useSymbolPlacementState needs useMapEditor's mapReady/activeMode/setMode/interactionRef)
    // needs a live ref to its current value — so the ref itself is created here and threaded
    // through both hooks; useSymbolPlacementState mirrors its state into it every render.
    const pendingTacticalSidcRef = useRef(null);

    const [showGeocoder, setShowGeocoder] = useState(false);

    // Ticket 05: "Show movement trail" global toggle + trailing-window-in-days control, local
    // (non-persisted) UI state — mirrors activeGrid's pattern above (a display preference, not
    // map-doc content). `trailWindowDays` is intentionally allowed to be '' transiently while
    // the user clears the field to type a new value (see MovementTrailControl's handleDaysChange);
    // useMapEditor is only fed a numeric value below (falls back to 7 while '').
    const [trailEnabled, setTrailEnabled] = useState(false);
    const [trailWindowDays, setTrailWindowDays] = useState(7);
    const effectiveTrailWindowDays = trailWindowDays === '' ? 7 : trailWindowDays;

    // Ticket 01: local (non-persisted) DTG scrub position for the bottom timeline.
    // Ticket 02 (map-timeline-range-rework): `start`/`end` are now user-editable via the
    // timeline's two DatePickers instead of a fixed demo window (see useDtgRange.js).
    // Ticket 03: wired into useMapEditor below as `currentDtg` — every new/edited marking
    // snapshot is stamped with this value instead of falling back to Date.now(). Declared
    // before useMapEditor() so its current value is available for that hook call.
    // shared-map-data ticket 02: `start`/`end` now seed from the shared map doc's `map_meta`
    // node (`dtgStartMs`/`dtgEndMs`) instead of from per-node props — every embed's editor shows
    // the same report-wide range. Read directly off `editorState.doc` here (captured once, at
    // mount, via this ref) rather than waiting for `useMapEditor`'s `meta` below, since that
    // hook itself needs this range's scrub value (`currentDtg`) as an input — `onRangeChange`
    // (fired only from real `setStart`/`setEnd` edits, never on mount) is wired to `updateMeta`
    // once `updateMeta` exists, via the effect further down, so user edits flow into the shared
    // doc through the same PM-transaction → debounced-Yjs-sync path layers/markings already use.
    // `dtgSelectedMs` (the scrub point) stays per-node, seeded from the originating node's own
    // persisted attr (threaded down from MapEditorPortal via session) exactly as before.
    const initialMapMetaRef = useRef(null);
    if (initialMapMetaRef.current === null) {
        initialMapMetaRef.current = editorState?.doc ? extractMapState(editorState.doc).meta : {};
    }
    // `updateMeta` (below, from useMapEditor) isn't created until after this hook call — see the
    // comment above for why the ordering can't simply be flipped — so `onRangeChange` reads it
    // through a ref that's assigned once useMapEditor runs, a few lines down.
    const updateMetaRef = useRef(null);
    const dtgRange = useDtgRange({
        start: initialMapMetaRef.current.dtgStartMs ?? undefined,
        end: initialMapMetaRef.current.dtgEndMs ?? undefined,
        initialValue: dtgSelectedMs ?? undefined,
        onRangeChange: (startMs, endMs) => updateMetaRef.current?.({ dtgStartMs: startMs, dtgEndMs: endMs }),
    });

    // Ticket 03: reports every change of the live scrub point up to MapEditorPortal (kept there
    // in a ref, not state) so its one-shot close handler can read the final value — this hook has
    // no other way to reach back out to the portal, which lives in a separate React tree with no
    // access to this component's local state. Firing on every change (including the initial
    // mount) is intentionally cheap: it only ever updates a ref, so the actual node-attrs write
    // still happens exactly once, at close.
    // shared-map-data ticket 02: only reports `valueMs` now — `startMs`/`endMs` no longer need a
    // commit-on-close round-trip since they're written live into the shared doc (see the
    // dtgRange onRangeChange -> updateMeta wiring below) rather than being read back out of a ref.
    // map-editor-url-state ticket 05: now skips its very first (mount-time) firing — that value is
    // just this session's already-persisted/seed dtgSelectedMs echoing back (MapEditorPortal's
    // `openSession` already seeds `dtgRangeRef`/the URL with it directly), not a real scrub. This
    // mirrors the `hasSyncedInitialViewRef` guard below for the same reason: skipping it isn't
    // just tidiness — a mount-time `setSearchParams` call here would race the portal's own
    // just-pushed `mapNode` (open) navigation, whose `setSearchParams` closure hasn't yet observed
    // that push, so its `{replace: true}` would clobber the push's target with a stale, `mapNode`-
    // less location (see MapEditorPortal.jsx's `useSearchParams` usage / react-router's
    // per-render-snapshot `searchParams` closure). By the time a REAL scrub happens, the portal has
    // long since re-rendered with the settled `mapNode` location, so no race exists past this one.
    const hasSyncedInitialDtgRef = useRef(false);
    useEffect(() => {
        if (!hasSyncedInitialDtgRef.current) {
            hasSyncedInitialDtgRef.current = true;
            return;
        }
        onDtgRangeChange?.({ valueMs: dtgRange.valueMs });
    }, [dtgRange.valueMs, onDtgRangeChange]);

    const {
        interactionRef, stateRef, layers, views, activeMode, activeLayerId,
        layerShapeCounts, selectedIds, selectedMarkings,
        setMode, setActiveLayer, mapReady,
        canUndo, canRedo,
        handleUpdateLayer, handleDeleteLayer, handleReorderLayer, handleAddLayer,
        handleUndo, handleRedo, handleDeleteSelected,
        createViewFromBbox, updateViewBbox, renameView, deleteView,
        updateMeta, updateMarkingProps, dispatch,
    } = useMapEditor({
        mapRef, editorState, setEditorState, currentUser, activeLayerIdRef, remoteChangeRef,
        symbolSettings: msSettings, pendingTacticalSidcRef, currentDtg: dtgRange.valueMs,
        trailEnabled, trailWindowDays: effectiveTrailWindowDays,
    });
    updateMetaRef.current = updateMeta;

    // shared-map-data ticket 03: title/basemapStyleId/overlayType are now per-embed "window"
    // settings that live on the originating mapNode's own attrs (report doc), not the shared map
    // doc's `map_meta` — each is seeded once from this session's initial value (threaded down
    // via MapEditorPortal/MapSession) and from then on locally owned React state, mirroring
    // useCoordGridOverlay's `activeGrid`. Every change is reported live via
    // `onWindowSettingsChange`, which MapNodeView writes straight onto the originating node's
    // attrs (see `_onWindowSettingsChange`) — unlike `dtgStartMs`/`dtgEndMs` (ticket 02), there's
    // no shared-doc round-trip involved, so no ref-batched close-time flush is needed here.
    const [title, setTitle] = useState(initialTitle || 'Untitled Map');
    const [basemap, setBasemap] = useState(initialBasemapStyleId || DEFAULT_BASEMAP);
    // ticket 01: 'mapbox' (default) | 'satellite' — see BasemapControl / applyImageryCoverage.
    const [overlayType, setOverlayType] = useState(initialOverlayType || OVERLAY_TYPES.MAPBOX);

    // Ticket 05: map init/teardown + race-safe basemap-style swap, extracted into its own hook.
    const { baseTileLayerRef } = useOlMapLifecycle({ mapContainerRef, mapRef, basemap });

    // Ticket 06: DTG-driven satellite imagery swap + throttled DTG persistence, extracted into
    // its own hook, composed right after useOlMapLifecycle per the ticket's dependency.
    useSatelliteImagery({
        mapRef, mapReady, baseTileLayerRef, dtgMs: dtgRange.valueMs, overlayType, onWindowSettingsChange,
    });

    // Ticket 07: coordinate-grid overlay (seed-from-prop + grid-draw/throttled-moveend), and
    // viewport bbox tracking for the Layers panel filter — both independent of each other,
    // composed after the map-lifecycle hook.
    const { activeGrid, setActiveGrid, gridCanvasRef } = useCoordGridOverlay({
        mapRef, mapReady, activeGridId: initialActiveGridId,
    });
    const viewportBbox = useViewportBboxTracking({ mapRef, mapReady });

    // Ticket 08: pending-SIDC / tactical draw state machine, extracted into its own hook.
    // Composed after useMapEditor since it needs mapReady/activeMode/setMode/interactionRef from
    // there; mirrors pendingTacticalSidc into pendingTacticalSidcRef (created above, pre-useMapEditor).
    const {
        pendingSidc, setPendingSidc,
        pendingTacticalSidc,
        pendingPointRange, pendingDrawMode, pendingDrawRule, pendingPlacedCount,
        handleEscape, handleSelectTacticalSidc, clearPendingSymbolSelection,
    } = useSymbolPlacementState({
        mapRef, mapContainerRef, mapReady, interactionRef, activeMode, setMode, pendingTacticalSidcRef,
    });

    // Ticket 09: saved-view fly-to/create/select/update-bbox, extracted into its own hook.
    const { selectedViewId, divergedBbox, flyToView, handleCreateView, handleSelectView, handleUpdateViewBbox, captureViewportOnClose } = useMapViews({
        mapRef, stateRef, vignetteFrameRef, mapReady, initialViewId: initialViewId ?? DEFAULT_VIEW_ID, bboxOverride, createViewFromBbox, updateViewBbox,
        // Bug fix (all-markings-dtg-crop): so "fit all markings"/"All markings" view resolves
        // each marking's position at the live scrub point, not its latest-ever position.
        currentDtg: dtgRange.valueMs,
    });

    // map-editor-url-state ticket 04: mirrors useMapViews' live divergence tracking out to
    // MapEditorPortal, which reflects it into the `mapBbox` URL param — a plain pass-through
    // effect (no persist-to-node write-back like the `viewId` one below, this is deliberately
    // URL-only/ephemeral) so it stays a single, easy-to-follow hop from "hook computed it" to
    // "portal heard about it".
    // map-editor-url-state ticket 07 (bugfix): skips its very first (mount-time) firing, same
    // reasoning/guard as `hasSyncedInitialDtgRef` above — `divergedBbox`'s initial value is always
    // `null` on mount (useMapViews seeds it that way and only sets it from a later `moveend`), so
    // there's nothing real to report yet, and reporting it anyway would race the portal's
    // just-pushed `mapNode` open navigation the same way. See that comment for the full race
    // explanation; this is the second of the two spots that needed it (this bug's actual repro:
    // opening the editor, closing it, then reopening — the reopen's `mapNode` push was getting
    // silently wiped by this effect's mount-time `onBboxChange(null)` echo).
    const hasSyncedInitialBboxRef = useRef(false);
    useEffect(() => {
        if (!hasSyncedInitialBboxRef.current) {
            hasSyncedInitialBboxRef.current = true;
            return;
        }
        onBboxChange?.(divergedBbox);
    }, [divergedBbox, onBboxChange]);

    // map-editor-url-state ticket 03: `viewId` joins basemap/grid/overlay/title as a live
    // write-back "window setting" — selecting a view in the left panel (or creating a new one,
    // which auto-selects it; see useMapViews.js's handleCreateView) now persists via the same
    // `onWindowSettingsChange` path those already use, rather than being purely local UI state
    // that's silently lost when the editor closes. Watches `selectedViewId` itself (rather than
    // wrapping `handleSelectView`/`handleCreateView` individually, like the direct-call pattern
    // `handleTitleChange`/`handleBasemapChange`/etc. use below) since useMapViews has more than
    // one internal path that can change it. Skips the very first value — that's just this
    // session's already-persisted/seed viewId echoing back, not a user-driven change.
    const hasSyncedInitialViewRef = useRef(false);
    useEffect(() => {
        if (!hasSyncedInitialViewRef.current) {
            hasSyncedInitialViewRef.current = true;
            return;
        }
        onWindowSettingsChange?.({ viewId: selectedViewId });
    }, [selectedViewId, onWindowSettingsChange]);

    // Ticket 09: drag-and-drop symbol placement onto the map canvas, extracted into its own hook.
    const { handleMapDrop, handleDragEnter, handleDragLeave, handleDragOver } = useSymbolDragDrop({
        mapRef, interactionRef,
    });

    const handleVignetteFrame = useCallback((rect) => { vignetteFrameRef.current = rect; }, []);

    useEditorKeyboardShortcuts(modalRef, {
        onUndo: handleUndo, onRedo: handleRedo, onDelete: handleDeleteSelected,
        extraShortcuts: { escape: handleEscape },
    });

    const activeLayerLabel = useMemo(() => {
        if (!activeLayerId) return null;
        const l = layers.find((ly) => String(ly.id) === activeLayerId);
        if (!l) return null;
        return l.properties?.label || l.properties?.name || null;
    }, [activeLayerId, layers]);

    // -- Ticket 06: Layers panel viewport + DTG filtering ---------------------
    // `layers`/`activeLayerId` etc. above come from useMapEditor already reshaped for the panel
    // (`layersForPanel` — `{id, properties}`), but `layersVisibleAtDtg` needs the raw `map_layer`/
    // `map_marking` node attrs (`layerId`, `snapshots`, `mobility`, ...) that only `extractMapState`
    // produces. `editorState` (the PM doc) is already a prop here, so re-derive that raw shape
    // directly rather than threading it through useMapEditor's return value.
    const { layers: rawLayersForFilter, markings: rawMarkingsForFilter } = useMemo(
        () => (editorState?.doc ? extractMapState(editorState.doc) : { layers: [], markings: [] }),
        [editorState],
    );

    const dtgValueMs = dtgRange.valueMs;

    // ticket 03 (layer-panel-rework): per-layer DTG start/end, recomputed whenever the raw
    // markings change (added/edited/deleted snapshots) — mirrors layerShapeCounts' "stays live"
    // requirement. Keyed by layerId to match layerShapeCounts' shape.
    const layerDtgRanges = useMemo(() => {
        const ranges = {};
        for (const l of rawLayersForFilter) {
            ranges[l.layerId] = computeLayerDtgRange(l.layerId, rawMarkingsForFilter);
        }
        return ranges;
    }, [rawLayersForFilter, rawMarkingsForFilter]);

    // ticket 01 (layer-panel-rework): same uid derivation used everywhere else in the map editor
    // (see useMapEditor.js's marking author/updatedBy and layer ownerId stamping) — LayerPanel
    // uses this to gate rename/delete on layer ownership.
    const currentUserId = currentUser?.uid || currentUser?.id || null;

    // The set of layerIds that qualify as "in view at the current viewport, within the selected
    // DTG range" per ticket 03's (map-timeline-range-rework) range-overlap filter — supersedes
    // ticket 06's single-point `layersVisibleAtDtg` now that the timeline has an editable
    // [start, end] range instead of a fixed scrub point.
    const inViewLayerIdSet = useMemo(
        () => new Set(
            layersVisibleInDtgRange(
                rawLayersForFilter, rawMarkingsForFilter, viewportBbox, dtgValueMs,
                dtgRange.startMs, dtgRange.endMs,
            ).map((l) => l.layerId),
        ),
        [rawLayersForFilter, rawMarkingsForFilter, viewportBbox, dtgValueMs, dtgRange.startMs, dtgRange.endMs],
    );

    // ticket 02 (layer-panel-audit): the sidebar Layers panel shows every layer belonging to this
    // document — `layersVisibleInDtgRange`/`inViewLayerIdSet` above answers a different question
    // ("what should actually render on the map canvas right now") and must not gate the panel
    // list itself. A hidden layer, or a brand-new layer with no shapes yet, must still show up
    // here immediately so users can find/unhide/draw on it. `layers` (from useMapEditor, already
    // panel-shaped `{id, properties}`) is passed straight through to LayerPanel below — no
    // filtering needed for that concern.

    // ticket 06 (layer-panel-audit): every layer excluded from `inViewLayerIdSet` — i.e. panned or
    // scrubbed out of the current viewport/DTG-qualifying window — should get the panel's
    // "out of view" visual treatment, not just the active/drawing-target layer (that was ticket
    // 06's original, narrower scope). This supersedes the old singular
    // `outOfViewActiveLayerId`/force-inclusion hack, which is no longer needed now that the panel
    // list itself is unfiltered per ticket 02 above.
    //
    // `inViewLayerIdSet` (via `layersVisibleInDtgRange`) also excludes layers purely because
    // they're hidden (`visible === false`) or have no shapes at all yet — those aren't "panned/
    // scrubbed away", they're just hidden or empty, and ticket 02 explicitly requires the panel to
    // distinguish those states (hidden -> closed-eye icon only; brand-new empty layer -> shows up
    // with no badge at all) rather than reusing this map-rendering-only filter as a stand-in.
    // Excluding both cases here keeps "out of view" meaning exactly one thing: this layer has
    // shapes and is currently shown, but they're outside the current pan/DTG window.
    const outOfViewLayerIds = useMemo(
        () => new Set(
            layers
                .filter((l) => {
                    const isHidden = l.properties?.visible === false;
                    const hasShapes = (layerShapeCounts[String(l.id)] ?? 0) > 0;
                    return !isHidden && hasShapes && !inViewLayerIdSet.has(String(l.id));
                })
                .map((l) => String(l.id)),
        ),
        [layers, inViewLayerIdSet, layerShapeCounts],
    );

    const handleTitleChange = useCallback((e) => { const next = e.target.value; setTitle(next); onWindowSettingsChange?.({ title: next }); }, [onWindowSettingsChange]);
    const handleBasemapChange = useCallback((url) => { setBasemap(url); onWindowSettingsChange?.({ basemapStyleId: url }); }, [onWindowSettingsChange]);
    const handleOverlayTypeChange = useCallback((type) => { setOverlayType(type); onWindowSettingsChange?.({ overlayType: type }); }, [onWindowSettingsChange]);
    const handleGridToggle = useCallback((gridId) => { setActiveGrid(gridId); onWindowSettingsChange?.({ activeGridId: gridId || null }); }, [setActiveGrid, onWindowSettingsChange]);
    const handleClose = useCallback(() => {
        // Persist the current OL viewport before closing so the small report-embed preview
        // always reflects the area the user last viewed in the editor (rather than reverting
        // to fit-all-markings or an old view bbox).
        if (mapReady) {
            const { viewId: committedViewId, newState } = captureViewportOnClose();
            // If a brand-new view was created for the "All markings" case, write the new
            // viewId back onto the originating mapNode so the preview renders to it.
            if (committedViewId) {
                onWindowSettingsChange?.({ viewId: committedViewId });
            }
            // Pass the updated PM state (with the view bbox persisted) straight to the
            // portal's pre-close flush so the Yjs fragment write includes the new view node
            // without waiting for a React render cycle to update editorStateRef.current.
            if (newState) {
                onBeforeClose?.(newState);
            }
        }
        onClose?.();
    }, [onClose, onBeforeClose, mapReady, captureViewportOnClose, onWindowSettingsChange]);
    const handleGeocoderToggle = useCallback(() => setShowGeocoder((v) => !v), []);
    const handleTrailToggle = useCallback((next) => setTrailEnabled(next), []);
    const handleTrailWindowDaysChange = useCallback((next) => setTrailWindowDays(next), []);

    // -- Mode change ----------------------------------------------------------
    // Geometry draw buttons are gone; only 'select' and 'place-symbol' remain
    // as toolbar-driven modes. All draw mode selection is symbol-driven.
    const handleModeChange = useCallback((modeName) => {
        if (modeName === 'place-symbol') { setMode(null); return; }
        clearPendingSymbolSelection();
        setMode(modeName === 'select' ? 'select' : null);
    }, [setMode, clearPendingSymbolSelection]);

    const handleSymbolUpdate = useCallback((markingId, props) => {
        // ticket 01: `affiliation` is derived from `sidc` inside `updateMarkingProps` itself (it
        // writes both into the resolved snapshot's `props`), so this handler just forwards the
        // raw patch through.
        updateMarkingProps(markingId, props);
    }, [updateMarkingProps]);

    // Manual-coordinate-entry: recenters the map after a successful Lat/Lon/MGRS edit in
    // SymbolEditor's "Position" fields — threaded down through ContextPanel.
    const handleFlyToCoordinate = useCallback((lonLat) => flyToLonLat(mapRef.current, lonLat), [mapRef]);

    // rerender-functional-setstate / advanced-event-handler-refs: stable ref so ContextPanel's
    // onDeselectAll prop doesn't change on every render.
    const handleDeselectAll = useCallback(() => interactionRef.current?.setSelectedIds([]), [interactionRef]);

    const toolbarActiveMode = pendingSidc ? 'place-symbol' : (activeMode === 'select' ? 'select' : null);

    return (
        <Stack ref={modalRef} tabIndex={-1} sx={{ position: 'absolute', inset: 0, bgcolor: 'background.paper', outline: 'none' }}>
            {/* Ticket 03: undo/redo/delete-selected no longer passed to EditorTitleBar — they're
                already rendered by the canvas MapDrawToolbar below, so EditorTitleBar no longer
                supports/renders those props at all (see EditorTitleBar.jsx). */}
            <EditorTitleBar title={title} onTitleChange={handleTitleChange} onClose={handleClose} centerSlot={<GeocoderSlot showGeocoder={showGeocoder} mapRef={mapRef} onToggle={handleGeocoderToggle} />} />
            <Stack direction="row" sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {/* Left sidebar */}
                <Stack sx={{ width: 220, flexShrink: 0, borderRight: 1, borderColor: 'divider', overflowY: 'auto' }}>
                    <Box sx={(theme) => ({ px: 1.5, py: 0.5, fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.disabled', borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: 'background.default' })}>Views</Box>
                    <ViewsPanel views={views} selectedViewId={selectedViewId} onSelectView={handleSelectView} onCreateView={handleCreateView} onUpdateViewCamera={handleUpdateViewBbox} onRenameView={renameView} onDeleteView={deleteView} onFlyToView={flyToView} />
                    <Box sx={(theme) => ({ px: 1.5, py: 0.5, fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.disabled', borderTop: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: 'background.default' })}>Layers</Box>
                    <LayerPanel layers={layers} activeLayerId={activeLayerId} outOfViewLayerIds={outOfViewLayerIds} hasLayersInDocument={layers.length > 0} layerShapeCounts={layerShapeCounts} layerDtgRanges={layerDtgRanges} currentUserId={currentUserId} onUpdateLayer={handleUpdateLayer} onDeleteLayer={handleDeleteLayer} onReorderLayer={handleReorderLayer} onSetActiveLayer={setActiveLayer} onAddLayer={handleAddLayer} isReadOnly={isReadOnly} />
                </Stack>

                {/* Map canvas */}
                <Box
                    sx={{ flex: 1, position: 'relative', minWidth: 0, overflow: 'hidden' }}
                    onDrop={handleMapDrop}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                >
                    <Box ref={mapContainerRef} sx={{ width: '100%', height: '100%' }} />

                    {mapReady && (
                        <MapDrawToolbar variant="canvas" activeMode={toolbarActiveMode} onModeChange={handleModeChange} onUndo={handleUndo} onRedo={handleRedo} onDeleteSelected={handleDeleteSelected} canUndo={canUndo} canRedo={canRedo} />
                    )}

                    {mapReady && pendingTacticalSidc && pendingDrawMode ? (
                        <PlacementWizard tacticalSidc={pendingTacticalSidc} pointRange={pendingPointRange} drawMode={pendingDrawMode} drawRule={pendingDrawRule} placedCount={pendingPlacedCount} />
                    ) : null}

                    {activeLayerLabel && mapReady && !pendingTacticalSidc ? (
                        <Chip label={`Drawing on: ${activeLayerLabel}`} size="small" color="primary" variant="outlined"
                            sx={{ position: 'absolute', bottom: 64, left: 12, zIndex: MAP_OVERLAY_Z.CONTROLS, fontSize: 11, height: 22, cursor: 'default', pointerEvents: 'none' }} />
                    ) : null}

                    {/* Ticket 01 (map-editor-modal-ux-audit): the three floating controls share one
                        real flex-column stacking container instead of each hand-positioning itself
                        via a magic `bottom` offset. Order below is top-to-bottom in on-screen terms
                        (MovementTrailControl highest, CoordGridControl nearest the corner) — the
                        container's `bottom`/`right` inset is fixed and its height grows upward as
                        children expand, so later siblings never get pushed under earlier ones and
                        no two controls' boxes can overlap regardless of expand/collapse state. */}
                    {mapReady && (
                        <Stack
                            direction="column"
                            alignItems="flex-end"
                            gap={1}
                            sx={{ position: 'absolute', bottom: MAP_CONTROL_COLUMN_INSET, right: MAP_CONTROL_COLUMN_INSET, zIndex: MAP_OVERLAY_Z.CONTROLS }}
                        >
                            <MovementTrailControl
                                enabled={trailEnabled}
                                onToggle={handleTrailToggle}
                                windowDays={trailWindowDays}
                                onWindowDaysChange={handleTrailWindowDaysChange}
                            />
                            <BasemapControl current={basemap} onChange={handleBasemapChange} overlayType={overlayType} onOverlayTypeChange={handleOverlayTypeChange} />
                            <CoordGridControl activeGrid={activeGrid} onToggle={handleGridToggle} />
                        </Stack>
                    )}
                    {mapReady && <MapTimeline range={dtgRange} />}

                    <canvas ref={gridCanvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: MAP_OVERLAY_Z.GRID_CANVAS }} />
                    <VignetteOverlay onFrame={handleVignetteFrame} />

                    {!mapReady && (
                        <Stack alignItems="center" justifyContent="center" gap={1.5} sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.45)', zIndex: MAP_OVERLAY_Z.LOADING }}>
                            <CircularProgress size={32} sx={{ color: '#fff' }} />
                            <Typography sx={{ color: '#fff', fontSize: 13 }}>Loading map…</Typography>
                        </Stack>
                    )}
                </Box>

                {/* Right sidebar — unified context panel */}
                <Stack data-no-shortcuts sx={(theme) => ({ width: 400, flexShrink: 0, borderLeft: `1px solid ${theme.palette.divider}`, overflow: 'hidden' })}>
                    <ContextPanel
                        pendingSidc={pendingSidc}
                        onSelectSidc={setPendingSidc}
                        pendingTacticalSidc={pendingTacticalSidc}
                        onSelectTacticalSidc={handleSelectTacticalSidc}
                        selectedMarkings={selectedMarkings}
                        onUpdateMarkingProps={handleSymbolUpdate}
                        onDeselectAll={handleDeselectAll}
                        onFlyToCoordinate={handleFlyToCoordinate}
                    />
                </Stack>
            </Stack>
        </Stack>
    );
};

const MapEditorModal = (props) => (
    <RendererSettingsProvider><MapEditorModalInner {...props} /></RendererSettingsProvider>
);

export default MapEditorModal;
